import React, { useState } from 'react';
import * as Icons from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from './ui/dialog';
import api from '../api/client';
import { toast } from 'sonner';

const LeadImportDialog = ({ trigger, onImported }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const r = await api.get('/leads/import/template.csv', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'kiana-leads-template.csv';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error('Could not download template');
    }
  };

  const submit = async () => {
    if (!file) { toast.error('Pick a .csv file first'); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/leads/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(r.data);
      toast.success(`Imported ${r.data.inserted} leads (${r.data.skipped} skipped, ${r.data.errors} errors)`);
      onImported && onImported(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setFile(null); setResult(null); } }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-xl" data-testid="lead-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Import Leads from CSV</DialogTitle>
          <DialogDescription>
            Bulk-add leads. Each row creates a new lead + auto-saves a contact. Duplicates (by phone) are skipped.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Required columns: <code>name</code>, <code>phone</code>. Optional: <code>alternate_mobile_number, email, source, status, priority, property, budget, profession, team_coordinator, notes</code>.
            <div className="mt-1">Set <code>team_coordinator</code> to the CRM user&apos;s exact name (e.g. AKSHAY METE) to assign the lead. If blank, the project&apos;s coordinator is auto-assigned.</div>
          </div>
          <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="lead-import-download-template">
            <Icons.Download className="w-4 h-4 mr-1.5" />Download CSV Template
          </Button>
          <div>
            <Label className="text-xs uppercase tracking-wide">CSV File</Label>
            <input
              type="file" accept=".csv"
              data-testid="lead-import-file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1 block w-full text-sm border border-slate-200 rounded-md px-3 py-2 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-brand-navy file:text-white"
            />
            {file && <div className="text-[11px] text-slate-500 mt-1">{file.name} · {Math.round(file.size / 1024)} KB</div>}
          </div>
          {result && (
            <div className="space-y-1 text-xs">
              <div className="font-medium">Result</div>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Inserted: {result.inserted}</span>
                <span className="px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200">Skipped: {result.skipped}</span>
                <span className="px-2 py-1 rounded bg-rose-50 text-rose-700 border border-rose-200">Errors: {result.errors}</span>
                {result.warnings > 0 && (
                  <span className="px-2 py-1 rounded bg-orange-50 text-orange-700 border border-orange-200" data-testid="lead-import-warnings">Unassigned: {result.warnings}</span>
                )}
              </div>
              {result.warning_rows?.length > 0 && (
                <pre className="bg-orange-50 border rounded p-2 max-h-32 overflow-auto" data-testid="lead-import-warning-rows">{JSON.stringify(result.warning_rows, null, 2)}</pre>
              )}
              {result.skipped_rows?.length > 0 && (
                <pre className="bg-slate-50 border rounded p-2 max-h-32 overflow-auto">{JSON.stringify(result.skipped_rows, null, 2)}</pre>
              )}
              {result.error_rows?.length > 0 && (
                <pre className="bg-rose-50 border rounded p-2 max-h-32 overflow-auto">{JSON.stringify(result.error_rows, null, 2)}</pre>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
          <Button onClick={submit} disabled={busy || !file} className="bg-brand-navy hover:bg-[#13294A]" data-testid="lead-import-submit">
            {busy ? 'Importing...' : 'Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LeadImportDialog;
